import { defineStore } from "pinia";
import axios from "axios";
import {
  BACKEND_URL,
  CHATBOT_FILTERS_ENDPOINT,
  CHATBOT_QUERY_ENDPOINT,
  CHATBOT_PREVIEW_LIMIT,
  CHATBOT_EXPORT_LIMIT,
} from "../variables.js";
import { postPayload } from "./MarketOverviewStore.js";

// Chat bot state and its two requests.
//
// Nothing here is persisted. The store lives for one app instance, and the
// results page opens in a new tab — a fresh instance with fresh Pinia — so the
// answers travel on that page's query string rather than through state. That
// also makes a result set linkable and bookmarkable, which state could not be.
//
// Answers are canonical option labels — never free text. The widget resolves
// what the user typed to a label before it reaches here, and the endpoint
// re-checks every label against its own vocabulary, so an answer that is not
// inside a filter cannot reach a query from either side.
export const useChatBotStore = defineStore("ChatBotStore", {
  state: () => ({
    // Question script from the backend: [{ key, label, question, multiple, options }]
    questions: [],
    questionsLoaded: false,
    // Set when the user's role has no access to the bot's dataset at all.
    noAccess: false,

    // filter key => array of canonical option labels
    answers: {},

    loading: false,
    // User-facing string on failure; the widget and the results page surface it.
    error: null,

    // Last query result.
    rows: [],
    total: 0,
    sql: "",
    // Values the endpoint refused, as human-readable notes. Should normally be
    // empty — the widget validates first — but a hand-edited results-page URL,
    // or a role change mid-session, both land here.
    rejected: [],
  }),

  getters: {
    // Questions that still have no answer, in script order.
    unanswered(state) {
      return state.questions.filter((q) => !(state.answers[q.key] || []).length);
    },
    hasAnswers(state) {
      return Object.values(state.answers).some((v) => (v || []).length);
    },
  },

  actions: {
    // Loads the question script once per session. Options come from the backend
    // rather than being duplicated here so a filter added to the vocabulary
    // reaches the conversation without a frontend change.
    async loadQuestions(force = false) {
      if (this.questionsLoaded && !force) return this.questions;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(BACKEND_URL + CHATBOT_FILTERS_ENDPOINT);
        if (!data || data.access === false) {
          this.noAccess = true;
          this.questions = [];
        } else {
          this.noAccess = false;
          this.questions = Array.isArray(data.filters) ? data.filters : [];
        }
        this.questionsLoaded = true;
      } catch (err) {
        console.error("Chat bot: failed to load filters", err);
        this.error = "Could not load the filter list. Please try again.";
      } finally {
        this.loading = false;
      }
      return this.questions;
    },

    setAnswer(key, values) {
      // Replaces rather than merges: the widget owns the accumulation while a
      // question is open, and a re-answered question should not silently keep
      // the values the user just cleared.
      this.answers = { ...this.answers, [key]: [...values] };
    },

    clearAnswer(key) {
      const next = { ...this.answers };
      delete next[key];
      this.answers = next;
    },

    reset() {
      this.answers = {};
      this.rows = [];
      this.total = 0;
      this.sql = "";
      this.rejected = [];
      this.error = null;
    },

    /**
     * Runs the collected answers against the endpoint.
     *
     * @param {object} opts
     *   { limit, offset, answers } — `answers` overrides the store's, which is
     *   what lets the results page run a URL-supplied set without first writing
     *   it into state.
     * @returns {object|null}
     *   The response payload, or null on failure (this.error is set).
     */
    async runQuery({ limit = CHATBOT_PREVIEW_LIMIT, offset = 0, answers = null } = {}) {
      const payload = {
        answers: answers || this.answers,
        limit: Math.min(limit, CHATBOT_EXPORT_LIMIT),
        offset,
      };
      this.loading = true;
      this.error = null;
      try {
        const { data } = await postPayload(
          BACKEND_URL + CHATBOT_QUERY_ENDPOINT,
          payload
        );
        if (!data || data.access === false) {
          this.noAccess = true;
          this.error = "You do not have access to this data.";
          return null;
        }
        this.rejected = Array.isArray(data.rejected) ? data.rejected : [];
        if (data.ok === false) {
          this.rows = [];
          this.total = 0;
          this.sql = "";
          this.error = data.message || "The query could not be run.";
          return data;
        }
        this.rows = Array.isArray(data.rows) ? data.rows : [];
        this.total = Number(data.total) || 0;
        this.sql = data.sql || "";
        return data;
      } catch (err) {
        console.error("Chat bot: query failed", err);
        this.error = "Could not run the search. Please try again.";
        return null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Every matching row, for the exports and the results page.
     *
     * Kept apart from runQuery so the widget's preview stays a small request:
     * the exports need the full set, the chat bubble needs the first handful.
     * Note this does load the full set into `rows` — the export path wants it
     * there anyway, and holding two row arrays would only let the table and the
     * file it exports drift apart.
     */
    async fetchAllRows(answers = null) {
      const data = await this.runQuery({
        limit: CHATBOT_EXPORT_LIMIT,
        offset: 0,
        answers,
      });
      return data && data.ok !== false ? this.rows : [];
    },
  },
});
